class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.swiperInstance = null;
    this.observer = null;
    this.cartDrawer = null; // Will be set in connectedCallback
    this.hasContent = false; // Track if recommendations loaded
  }

  connectedCallback() {
    const productId = this.dataset.productId;
    const sectionId = this.dataset.sectionId;
    const target = this.dataset.target;
    const limit = this.dataset.limit || 4;
    const intent = this.dataset.intent || 'related';

    // Setup cart drawer reference and observer
    this.setupDrawerObserver();

    // Check if recommendations already rendered server-side
    const existingContainer = this.querySelector(`[class*="${target}"]`);
    
    if (existingContainer && existingContainer.innerHTML.trim().length > 0) {
      console.log('Server-rendered recommendations found, initializing Swiper');
      this.hasContent = true;
      this.initializeSwiper();
      this.updateActiveState(); // Check initial state
      return;
    }

    if (!productId) {
      console.error('Product ID is required for recommendations');
      return;
    }

    this.fetchRecommendations(productId, sectionId, target, limit, intent);
  }

  disconnectedCallback() {
    if (this.abortController) {
      this.abortController.abort();
    }
    
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
    }

    if (this.observer) {
      this.observer.disconnect();
    }
  }

  /**
   * Sets up MutationObserver to watch cart drawer state changes
   */
  setupDrawerObserver() {
    this.cartDrawer = document.querySelector('.cart-drawer-sheet-content');
    
    if (!this.cartDrawer) {
      console.warn('Cart drawer not found');
      return;
    }

    // Create observer to watch for data-state attribute changes
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-state') {
          this.updateActiveState();
        }
      });
    });

    // Start observing the cart drawer
    this.observer.observe(this.cartDrawer, { 
      attributes: true,
      attributeFilter: ['data-state'] // Only watch data-state changes
    });

    // Check initial state immediately
    this.updateActiveState();
  }

  /**
   * Updates the active class based on drawer state and content availability
   */
  updateActiveState() {
    if (!this.cartDrawer) {
      return;
    }

    const isDrawerOpen = this.cartDrawer.getAttribute('data-state') === 'open';
    
    // Only add active class if drawer is open AND we have content to show
    if (isDrawerOpen && this.hasContent) {
      this.classList.add('active');
    } else {
      this.classList.remove('active');
    }
  }

  async fetchRecommendations(productId, sectionId, target, limit, intent) {
    this.abortController = new AbortController();
    
    try {
      const url = `${window.Shopify.routes.root}recommendations/products?product_id=${productId}&limit=${limit}&section_id=${sectionId}&intent=${intent}`;
      
      const response = await fetch(url, {
        signal: this.abortController.signal
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      
      // Try to find the target class in the response
      const recommendations = html.querySelector(`.${target}`) || 
                            html.querySelector('.cart-product-recommendations-desktop-container');
      
      if (recommendations && recommendations.innerHTML.trim().length > 0) {
        this.innerHTML = recommendations.innerHTML;
        this.hasContent = true;
        this.initializeSwiper();
        
        // Update active state now that content is loaded
        this.updateActiveState();
      } else {
        console.log('No recommendations found for product:', productId);
        this.hasContent = false;
        this.style.display = 'none';
        this.updateActiveState();
        
        this.dispatchEvent(new CustomEvent('recommendations:empty', {
          bubbles: true,
          detail: { productId }
        }));
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      
      console.error('Error fetching product recommendations:', error);
      this.hasContent = false;
      this.style.display = 'none';
      this.updateActiveState();
      
      this.dispatchEvent(new CustomEvent('recommendations:error', {
        bubbles: true,
        detail: { error, productId }
      }));
    }
  }

  initializeSwiper() {
    requestAnimationFrame(() => {
      const cartSwiperEl = this.querySelector('.cart-product-recommendations-swiper');
      const productSwiperEl = this.querySelector('.product-recommendations__swiper');
      const nextButton = this.querySelector('.swiper-button-next');
      const prevButton = this.querySelector('.swiper-button-prev');
      const paginationEl = this.querySelector('.swiper-pagination');
      
      let swiperEl = null;
      let config = {};

      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
      }

      if (cartSwiperEl) {
        swiperEl = cartSwiperEl;
        config = {
          loop: true,
          grabCursor: true,
          slidesPerView: 1,
          spaceBetween: 16,
          pagination: {
            el: paginationEl,
            type: 'bullets',
            dynamicBullets: true,
            dynamicMainBullets: 2,
            clickable: true,
          },
        };
      } else if (productSwiperEl) {
        swiperEl = productSwiperEl;
        config = {
          slidesPerView: 1.3,
          spaceBetween: 16,
          navigation: {
            nextEl: nextButton,
            prevEl: prevButton,
          },
          pagination: {
            el: paginationEl,
            type: "progressbar",
          },
          breakpoints: {
            1024: {
              slidesPerView: 4,
              spaceBetween: 32,
            },
          },
        };
      } else {
        return;
      }

      this.swiperInstance = new Swiper(swiperEl, config);
    });
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}